import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  DestroyRef,
  ElementRef,
  Input,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MathTextComponent } from '../math-text.component';
import { hasBold, hasMath, splitMath } from '../math-text';

type TextField = HTMLTextAreaElement | HTMLInputElement;

/** Ce que l'on retient d'un `<math-field>` MathLive — son API complète est inutile ici. */
interface MathField extends HTMLElement {
  value: string;
  mathVirtualKeyboardPolicy: string;
  menuItems: unknown[];
  getValue(format: 'latex-expanded'): string;
}

/** Ce que l'on retient du clavier virtuel MathLive, singleton posé sur `window`. */
interface VirtualKeyboard extends EventTarget {
  container: HTMLElement;
  readonly visible: boolean;
  readonly boundingRect: DOMRect;
  hide(): void;
}

function virtualKeyboard(): VirtualKeyboard | undefined {
  return (window as unknown as { mathVirtualKeyboard?: VirtualKeyboard }).mathVirtualKeyboard;
}

/**
 * Saisie riche d'un texte de question : gras, retours à la ligne, formules.
 *
 * <b>Le champ reste celui de l'écran.</b> Le composant n'en fournit pas un
 * autre : il enveloppe, par projection, le `<textarea>` ou l'`<input>` que le
 * gabarit déclarait déjà, avec ses classes, son style et sa liaison au
 * formulaire (`formControlName` ou `ngModel`). Il ne fait qu'ajouter une barre
 * d'outils au-dessus, un aperçu au-dessous et la fenêtre de formule. Retirer
 * l'enveloppe rendrait l'écran exact d'avant.
 *
 * ```html
 * <app-rich-field>
 *   <textarea class="form-control qe__control" formControlName="title"></textarea>
 * </app-rich-field>
 * ```
 *
 * La valeur reste du texte brut : formule entre `\( … \)` ou `\[ … \]`, gras
 * entre `**`. C'est le format que l'import produit et que `app-math-text`
 * affiche. Les retours à la ligne sont ceux du `<textarea>`, rien à faire.
 *
 * La formule se compose dans MathLive — éditeur visuel : `/` fait une
 * fraction, `^` un exposant, `_` un indice, avec un clavier virtuel sur
 * tablette. Il n'est chargé qu'à la première ouverture de la fenêtre.
 */
@Component({
  selector: 'app-rich-field',
  standalone: true,
  imports: [TranslateModule, MathTextComponent],
  templateUrl: './rich-field.component.html',
  styleUrl: './rich-field.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichFieldComponent implements AfterContentInit {
  /**
   * Variante d'une ligne d'option de QCM : un seul bouton de formule, accolé
   * au champ, sans barre d'outils — quatre barres pour quatre options
   * noieraient la liste.
   */
  @Input() compact = false;

  @ContentChild(NgControl) private ngControl?: NgControl;
  @ViewChild('dialog') private dialogRef?: ElementRef<HTMLDialogElement>;
  @ViewChild('mathHost') private mathHostRef?: ElementRef<HTMLElement>;
  @ViewChild('keyboardHost') private keyboardHostRef?: ElementRef<HTMLElement>;
  private onKeyboardGeometry?: () => void;

  readonly value = signal('');
  readonly showPreview = computed(() => hasMath(this.value()) || hasBold(this.value()));

  /** État de la fenêtre de formule. */
  readonly latex = signal('');
  readonly display = signal(false);
  readonly editing = signal(false);
  readonly loadError = signal(false);
  readonly tooLong = signal(false);
  /** Une case de la formule reste vide : l'élève verrait `\placeholder` en rouge. */
  readonly incomplete = computed(() => this.latex().includes('\\placeholder'));
  readonly delimited = computed(() => this.wrap(this.latex().trim(), this.display()));

  multiline = true;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  private field?: TextField;
  private mathField?: MathField;
  /** Portion du texte que la formule remplacera : sélection, ou formule existante. */
  private range = { start: 0, end: 0 };

  ngAfterContentInit(): void {
    this.field = this.host.nativeElement.querySelector<TextField>('textarea, input') ?? undefined;
    if (!this.field) return;

    this.multiline = this.field.tagName === 'TEXTAREA';

    const sync = () => this.value.set(this.currentValue());
    const onKeydown = (event: Event) => this.onKeydown(event as KeyboardEvent);

    this.field.addEventListener('input', sync);
    this.field.addEventListener('keydown', onKeydown);

    // `ngModel` pose sa valeur après une microtâche, un `patchValue` sans
    // événement de saisie : on suit aussi le contrôle.
    const subscription = this.ngControl?.valueChanges?.subscribe(sync);
    queueMicrotask(sync);

    this.destroyRef.onDestroy(() => {
      this.field?.removeEventListener('input', sync);
      this.field?.removeEventListener('keydown', onKeydown);
      subscription?.unsubscribe();
    });
  }

  // ── Barre d'outils ─────────────────────────────────────────────────────

  /** Met en gras la sélection, ou ouvre une paire de marques au curseur. */
  bold(): void {
    const field = this.field;
    if (!field) return;

    const text = this.currentValue();
    const start = field.selectionStart ?? text.length;
    const end = field.selectionEnd ?? start;
    const selected = text.slice(start, end);

    // Déjà en gras : on retire les marques.
    if (selected.startsWith('**') && selected.endsWith('**') && selected.length >= 4) {
      this.replace(start, end, selected.slice(2, -2), start, end - 4);
      return;
    }

    const inner = selected.trim().length > 0 ? selected : '';
    const caret = inner ? start + inner.length + 4 : start + 2;
    this.replace(start, end, `**${inner}**`, inner ? start : caret, caret);
  }

  /**
   * Ouvre la fenêtre de formule. Si le curseur est dans une formule existante,
   * c'est elle qu'on modifie ; sinon la sélection devient le point de départ.
   */
  async openFormula(display: boolean): Promise<void> {
    const field = this.field;
    if (!field) return;

    const text = this.currentValue();
    const start = field.selectionStart ?? text.length;
    const end = field.selectionEnd ?? start;

    const existing = splitMath(text).find(s => s.math && start > s.start && start < s.end);

    if (existing) {
      this.range = { start: existing.start, end: existing.end };
      this.latex.set(existing.value);
      this.display.set(existing.display);
      this.editing.set(true);
    } else {
      this.range = { start, end };
      this.latex.set(text.slice(start, end).trim());
      this.display.set(display && this.multiline);
      this.editing.set(false);
    }

    this.dialogRef?.nativeElement.showModal();
    await this.mountMathField();
  }

  /**
   * Insère (ou remplace) la formule en cours dans le texte. Refusé — et dit
   * dans la fenêtre — tant qu'une case reste à remplir, ou si le champ
   * dépasserait sa longueur maximale.
   */
  confirm(): void {
    const latex = this.latex().trim();
    if (!latex || this.incomplete()) return;

    if (!this.insertFormula(latex, this.display())) {
      this.tooLong.set(true);
      return;
    }

    this.close();
  }

  /** Retire la formule en cours d'édition. */
  remove(): void {
    const { start, end } = this.range;
    this.replace(start, end, '', start, start);
    this.close();
  }

  close(): void {
    this.dialogRef?.nativeElement.close();
  }

  onClosed(): void {
    virtualKeyboard()?.hide();
    this.dialogRef?.nativeElement.style.removeProperty('--rf-keyboard-height');
    this.field?.focus();
  }

  /** Vrai si l'appui en cours a commencé sur le voile, hors du panneau. */
  private pressedOnBackdrop = false;

  onDialogPointerDown(event: PointerEvent): void {
    this.pressedOnBackdrop = event.target === this.dialogRef?.nativeElement;
  }

  /**
   * Un clic hors du panneau ferme la fenêtre. La fenêtre couvre tout l'écran :
   * un clic qui l'atteint directement est un clic sur le voile.
   *
   * <p>L'appui doit lui aussi avoir commencé sur le voile. Une sélection dans
   * le code LaTeX relâchée hors du panneau produit un clic adressé à la
   * fenêtre — l'ancêtre commun —, qui la fermait et perdait la formule.</p>
   */
  onDialogClick(event: MouseEvent): void {
    const dialog = this.dialogRef?.nativeElement;
    const fromBackdrop = this.pressedOnBackdrop;
    this.pressedOnBackdrop = false;

    if (fromBackdrop && event.target === dialog) this.close();
  }

  onLatexInput(value: string): void {
    this.setLatex(value);
    if (this.mathField && this.mathField.value !== value) this.mathField.value = value;
  }

  /**
   * Écrit la formule à la place de la portion retenue. Une formule centrée
   * occupe sa propre ligne : on l'isole par des retours si besoin. Rend
   * `false`, sans rien écrire, si le champ dépasserait sa longueur maximale.
   */
  insertFormula(latex: string, display: boolean): boolean {
    const text = this.currentValue();
    const { start, end } = this.range;

    let insertion = this.wrap(latex, display && this.multiline);
    if (display && this.multiline) {
      if (start > 0 && text[start - 1] !== '\n') insertion = `\n${insertion}`;
      if (end < text.length && text[end] !== '\n') insertion = `${insertion}\n`;
    }

    const caret = start + insertion.length;
    return this.replace(start, end, insertion, caret, caret);
  }

  /** Portion de texte que la prochaine formule remplacera — exposé pour les tests. */
  setRange(start: number, end: number): void {
    this.range = { start, end };
  }

  // ── Interne ────────────────────────────────────────────────────────────

  private onKeydown(event: KeyboardEvent): void {
    if (!(event.ctrlKey || event.metaKey) || event.altKey) return;

    // La touche physique, pas le caractère : sur une disposition arabe,
    // Ctrl + B produit « لا » et `event.key` ne vaudrait jamais « b ».
    if (event.code === 'KeyM') {
      event.preventDefault();
      void this.openFormula(false);
    } else if (event.code === 'KeyB' && !this.compact) {
      event.preventDefault();
      this.bold();
    }
  }

  private wrap(latex: string, display: boolean): string {
    return display ? `\\[${latex}\\]` : `\\(${latex}\\)`;
  }

  private currentValue(): string {
    const fromControl = this.ngControl?.value;
    return typeof fromControl === 'string' ? fromControl : (this.field?.value ?? '');
  }

  /**
   * Remplace une portion du texte et replace le curseur.
   *
   * La valeur passe par un événement `input` sur le champ lui-même : c'est la
   * voie qu'emprunte une frappe au clavier, donc celle que comprennent aussi
   * bien `formControlName` que `[ngModel]` + `(ngModelChange)`.
   *
   * Une valeur posée par programme échappe au `maxlength` du champ : on le
   * vérifie ici, sans quoi une formule ferait dépasser la limite que l'écran
   * annonce et que le serveur refuse.
   */
  private replace(start: number, end: number, insertion: string, caretStart: number, caretEnd: number): boolean {
    const field = this.field;
    if (!field) return false;

    const text = this.currentValue();
    const next = text.slice(0, start) + insertion + text.slice(end);
    if (field.maxLength > 0 && next.length > field.maxLength && next.length > text.length) return false;

    field.value = next;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    this.value.set(field.value);

    field.focus();
    field.setSelectionRange(caretStart, caretEnd);
    return true;
  }

  private setLatex(value: string): void {
    this.latex.set(value);
    this.tooLong.set(false);
  }

  private async mountMathField(): Promise<void> {
    const host = this.mathHostRef?.nativeElement;
    const dialog = this.dialogRef?.nativeElement;
    if (!host || !dialog) return;

    try {
      if (!this.mathField) {
        const mathlive = await import('mathlive');
        const element = mathlive.MathfieldElement;

        // Les polices sont copiées dans les assets au build ; les sons, inutiles.
        element.fontsDirectory = new URL('assets/mathlive/fonts/', document.baseURI).href;
        element.soundsDirectory = null;
        const lang = this.translate.currentLang || this.translate.defaultLang;
        if (lang) element.locale = lang;

        const field = new element() as unknown as MathField;
        field.mathVirtualKeyboardPolicy = 'auto';
        // Forme développée : les macros propres à MathLive (\differentialD,
        // \exponentialE…), que son clavier insère, deviennent du LaTeX que
        // KaTeX sait composer. Les cases vides (\placeholder{}) restent : elles
        // bloquent l'insertion tant qu'elles ne sont pas remplies.
        field.addEventListener('input', () => this.setLatex(field.getValue('latex-expanded')));
        host.append(field);
        this.mathField = field;

        // Pas de menu contextuel : il s'ouvrait hors de la fenêtre, à moitié
        // non traduit, et le clavier virtuel couvre le même besoin. À poser
        // une fois le champ dans la page : avant, MathLive lève une exception,
        // qui faisait passer l'éditeur visuel pour introuvable. La feuille de
        // style masque de toute façon le bouton du menu.
        try {
          field.menuItems = [];
        } catch {
          /* le bouton reste masqué par la feuille de style */
        }
      }

      // Le clavier virtuel s'affiche dans la fenêtre, en bas : posé dans la
      // page, il passerait sous la couche supérieure qu'occupe une modale
      // native. Quand il s'ouvre, la fenêtre lui réserve sa hauteur et le
      // panneau se recentre au-dessus au lieu d'être recouvert.
      const keyboard = virtualKeyboard();
      const keyboardHost = this.keyboardHostRef?.nativeElement;
      if (keyboard && keyboardHost) {
        keyboard.container = keyboardHost;

        if (!this.onKeyboardGeometry) {
          const listener = () => {
            if (!dialog.open) return;
            const height = keyboard.visible ? Math.round(keyboard.boundingRect.height) : 0;
            dialog.style.setProperty('--rf-keyboard-height', `${height}px`);
          };
          this.onKeyboardGeometry = listener;
          keyboard.addEventListener('geometrychange', listener);
          this.destroyRef.onDestroy(() => keyboard.removeEventListener('geometrychange', listener));
        }
      }

      this.loadError.set(false);
      this.mathField.value = this.latex();
      requestAnimationFrame(() => this.mathField?.focus());
    } catch {
      // Sans l'éditeur visuel, la saisie LaTeX directe reste possible.
      this.loadError.set(true);
    }
  }
}
